import { FileTranscripts, LibraryFile } from './types';
import { getCurrentOutputLanguageName } from '../../../../hooks/useOutputLanguage';

export const timeToSeconds = (timestamp: string): number => {
  const [hours, minutes, seconds] = timestamp.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const getPromptText = async (
  fileTranscripts: FileTranscripts,
  files: LibraryFile[],
  directions: string,
) => {
  // Get current output language setting
  const outputLanguageName = await getCurrentOutputLanguageName();
  let transcriptText = '';
  Object.entries(fileTranscripts).forEach(([fileId, transcript], index) => {
    const file = files.find((f) => f._id === fileId);
    const fileName = file?.name || 'Unknown File';
    const date = file?.createdAt
      ? new Date(file.createdAt).toISOString().split('T')[0]
      : 'Unknown Date';

    // Parse the transcript JSON
    const parsedTranscript = JSON.parse(transcript);
    const segments = parsedTranscript.segments || [];

    // Convert segments to XML format with speaker and timestamp info
    const textWithSegments = segments
      .map((segment: any) => {
        const startTime = Math.floor(segment.start);
        const speaker = segment.speaker || 'UNKNOWN';
        const text = segment.text.trim();

        return `<segment time="${startTime}" speaker="${speaker}">${text}</segment>`;
      })
      .join('\n');

    transcriptText += `<transcript 
      number="${index + 1}" 
      id="${fileId}"
      filename="${fileName}" 
      date="${date}"
    >${textWithSegments}</transcript>\n`;
  });

  const transcriptCount = Object.keys(fileTranscripts).length;

  return `
<prompt>
  <critical_priority>
    **MATCH YOUR RESPONSE TO THE USER'S REQUEST:**

    1. **Analyze the user's task to determine the appropriate response type:**
       • Simple question (e.g., "What was discussed?", "Who attended?", "What's the main topic?")
         → Provide a direct, concise answer in 1-3 sentences with citation

       • Specific request (e.g., "List action items", "Find problems", "What are the goals?")
         → Provide a focused response addressing only that specific aspect

       • Summary request (e.g., "Summarize", "Give me an overview", "What are the key points?")
         → Provide a brief summary with main points (3-7 bullet points typically)

       • Report/comprehensive analysis (e.g., "Create a report", "Detailed analysis", "Full breakdown")
         → Provide structured, comprehensive response with sections and subsections

    2. **DEFAULT TO BREVITY:**
       • Provide EXACTLY what was asked for - nothing more, nothing less
       • If the user asks a question, answer it directly first
       • Only add supporting details if they're essential to understanding
       • NEVER create a multi-section report unless explicitly requested

    3. **Examples of appropriate responses:**

       User: "What was the main topic?"
       ✓ Good: "The main topic was Q4 budget allocation, focusing on marketing spend [→](abc123@45)"
       ✗ Bad: "## Meeting Overview\n\nThe meeting covered several important topics..."

       User: "List the action items"
       ✓ Good: "**Action Items:**\n• John to draft proposal by Friday [→](abc123@120)\n• Sarah to review budget [→](abc123@145)"
       ✗ Bad: "## Comprehensive Analysis\n\n### Action Items\n\nDuring the meeting, several action items were discussed..."

       User: "Summarize the key points"
       ✓ Good: "**Key Points:**\n• Budget approved for Q4\n• New hire starting next month\n• Project deadline extended"

       User: "Create a detailed report"
       ✓ Good: [Full structured report with multiple sections]
  </critical_priority>

  <task>${directions}</task>

  <context>
    You are an expert analyst and knowledge synthesizer. Your task is to analyze recordings and extract insights with precision and clarity.

    ## Language Requirement
    • Respond in ${outputLanguageName}
    • ALL text must be in ${outputLanguageName}
    • Maintain professional terminology appropriate for ${outputLanguageName}

    ## Format Requirements
    • NEVER wrap your entire response in a markdown code block
    • Write as plain text with markdown formatting
    • DO NOT start your response with \`\`\`markdown or any other code block indicator
  </context>

  <input>
    You have ${transcriptCount} recording${transcriptCount > 1 ? 's' : ''} to analyze. Each recording is provided in XML format with metadata including:
    • **id**: unique identifier for the recording
    • **filename**: name of the recording
    • **date**: when the recording was created
    • **segments**: transcript text with timestamps and speaker information
  </input>

  <response_guidelines>
    ### Response Structure (Adapt to Request Type)

    **For Simple Questions:**
    • Direct answer in 1-3 sentences
    • Include 1-2 citations to support the answer
    • No headers or elaborate structure needed

    **For Specific Requests (action items, problems, goals, etc.):**
    • Brief intro line (optional)
    • Focused list or short paragraphs addressing the specific request
    • Only include what was asked for

    **For Summaries:**
    • Brief opening statement (1 sentence)
    • 3-7 key points as bullet list
    • Keep each point concise (1-2 lines)

    **For Reports/Comprehensive Analysis:**
    • Use clear headers (##, ###) to organize sections
    • Create structured sections by topic
    • Include detailed insights with supporting evidence
    • Use formatting: **bold** for key terms, *italic* for emphasis, > for quotes

    ### Formatting Tools
    • **Bold** for key terms and important insights
    • *Italic* for emphasis
    • Bullet lists (•) for discrete points
    • Numbered lists (1., 2.) for sequential items
    • > for important quotes or callouts
    • DO NOT use code blocks or backticks for regular text

    ### Content Principles
    • Be concise and avoid redundancy
    • Prioritize actionable information
    • Include only the most relevant details
    • Cite sources for key claims
    • Match detail level to the complexity of the request

    ### Citations (CRITICAL)

    **Format Rules:**
    • Citations MUST use the recording's ID, not the filename
    • Format: [→](file_id@seconds)
    • Examples:
      ✓ [→](abc123@90)
      ✗ [→](Meeting Recording@90)
    • NEVER use square brackets at the end: [→](abc123@42]

    **Timestamp Validation:**
    • Verify timestamps are within the recording's duration
    • Most recordings are 15-90 minutes
    • Timestamps > 7200 seconds (2 hours) require verification
    • Use valid timestamps only

    **Speaker Identification:**
    • NEVER use "SPEAKER_01", "SPEAKER_1", "Speaker A"
    • Use descriptive identifiers:
      1. Role and name if known: "Product Manager Sarah"
      2. Role if name unknown: "Product Manager"
      3. Descriptive characteristics: "Senior Team Member", "First Engineer"
    • Be consistent throughout the response

    ### Quality Standards
    • Ensure factual accuracy
    • Present information clearly
    • Preserve original terminology
    • Maintain logical flow
    • Avoid meta-commentary about the analysis process
    • Focus on actionable insights
  </response_guidelines>
</prompt>

${transcriptText}`;
};

export const SUGGESTIONS = [
  'drawer.multiAskAI.suggestions.summarizeCommonalities',
  'drawer.multiAskAI.suggestions.getActionItems',
  'drawer.multiAskAI.suggestions.createReport',
  'drawer.multiAskAI.suggestions.findProblems',
  'drawer.multiAskAI.suggestions.listGoals',
];
